import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Grid,
} from "@mui/material";
import { ChevronRight, ChevronLeft } from "@mui/icons-material";
import { ProviderWithContactInfo } from "../details-page.types";
import SimilarProviderCard from "./SimilarProviderCard";
import { useNavigate } from "react-router-dom";
import "./SimilarProvidersCarousel.scss";
interface Props {
  providers: ProviderWithContactInfo[];
}

const SimilarProvidersCarousel: React.FC<Props> = ({ providers }) => {
  // CONSTANTS
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const itemsToShow = isMobile ? 1 : isSm ? 2 : 4;

  const [startIndex, setStartIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);

  const navigate = useNavigate();

  const trackRef = useRef<HTMLDivElement>(null);

  // Measure card width on mount and resize
  useEffect(() => {
    const updateCardWidth = () => {
      if (trackRef.current) {
        const firstCard = trackRef.current.querySelector(
          ".similar-providers-carousel__card-wrapper",
        ) as HTMLElement;
        if (firstCard) {
          setCardWidth(firstCard.offsetWidth);
        }
      }
    };

    updateCardWidth();
    window.addEventListener("resize", updateCardWidth);
    return () => window.removeEventListener("resize", updateCardWidth);
  }, [itemsToShow]);

  const handleNextSlide = () => {
    setStartIndex((prev) =>
      Math.min(prev + itemsToShow, providers.length - itemsToShow),
    );
  };

  const handlePrevSlide = () => {
    setStartIndex((prev) => Math.max(prev - itemsToShow, 0));
  };

  const handleViewDetails = (id: number) => {
    // navigating to particular provider's details page
    navigate(`/home/list-of-funeral-homes/details/${id}`);
  };

  const handleContactInfo = (id: number) => {};

  const handleViewAll = () => {
    // navigating to the providers listing page
    navigate("/home/list-of-funeral-homes");
  };

  const isAtStart = startIndex === 0;
  const isAtEnd = startIndex + itemsToShow >= providers.length;

  if (!providers || providers?.length === 0) {
    return (
      <Box className="similar-providers-carousel__container">
        <Box className="similar-providers-carousel__header">
          <Typography className="similar-providers-carousel__title">
            Similar Providers you might be interested in
          </Typography>
          <Typography
            className="similar-providers-carousel__view-all"
            onClick={() => navigate("/home/list-of-funeral-homes")}
          >
            View All
          </Typography>
        </Box>

        <Box className="similar-providers-carousel__empty">
          <Typography className="similar-providers-carousel__empty-text">
            No similar providers found
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="similar-providers-carousel__container">
      <Box className="similar-providers-carousel__header">
        {isMobile ? (
          <Typography className="similar-providers-carousel__mobile-title">
            Similar Providers you might be interested in
          </Typography>
        ) : (
          <Typography className="similar-providers-carousel__title">
            Similar Providers you might be interested in
          </Typography>
        )}

        {isMobile ? (
          <Typography
            className="similar-providers-carousel__mobile-view-all"
            onClick={handleViewAll}
          >
            View All
          </Typography>
        ) : (
          <Typography
            className="similar-providers-carousel__view-all"
            onClick={handleViewAll}
          >
            View All
          </Typography>
        )}
      </Box>

      <Box className="similar-providers-carousel__carousel">
        {/* Track wrapper */}
        <Box
          className="similar-providers-carousel__track"
          ref={trackRef}
          style={{
            transform: `translateX(-${(startIndex * 100) / providers.length}%)`,
            width: `${(providers.length * 100) / itemsToShow}%`,
          }}
        >
          {providers.map((provider, index) => (
            <Grid
              key={`${provider.id}-${index}`}
              className="similar-providers-carousel__card-wrapper"
              style={{
                width: `${100 / providers.length}%`,
              }}
            >
              <SimilarProviderCard
                provider={provider}
                onViewDetails={handleViewDetails}
                onContactInfo={handleContactInfo}
              />
            </Grid>
          ))}
        </Box>

        {/* Navigation Arrows */}
        <IconButton
          onClick={handlePrevSlide}
          disabled={isAtStart}
          className={`similar-providers-carousel__arrow similar-providers-carousel__arrow--left ${
            isAtStart ? "similar-providers-carousel__arrow--disabled" : ""
          }`}
        >
          <ChevronLeft />
        </IconButton>

        <IconButton
          onClick={handleNextSlide}
          disabled={isAtEnd}
          className={`similar-providers-carousel__arrow similar-providers-carousel__arrow--right ${
            isAtEnd ? "similar-providers-carousel__arrow--disabled" : ""
          }`}
        >
          <ChevronRight />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SimilarProvidersCarousel;
